<template>
  <div class="input-group">
    <flat-pickr
      v-model="date"
      class="form-control"
      :placeholder="placeholder"
      :config="flatpickrConfig"
      @on-change="onChange"
    />
    <div
      v-if="date"
      class="input-group-append"
    >
      <BButton
        variant="outline-secondary"
        class="px-1 flat-picker-clear-btn"
        @click="onDateClear"
      >
        <feather-icon icon="XIcon" />
      </BButton>
    </div>
  </div>
</template>

<script>
import flatPickr from 'vue-flatpickr-component'
import { BButton } from 'bootstrap-vue'

export default {
  components: {
    flatPickr,
    BButton,
  },
  props: {
    value: {
      type: [String, Object, Array, null],
      default: '',
    },
    config: {
      type: Object,
      default: () => ({}),
    },
    placeholder: {
      type: String,
      default: 'Select Date',
    },
  },
  data() {
    return {
      date: this.value,
    }
  },
  computed: {
    flatpickrConfig() {
      // Check if time is enabled
      const hasTime = this.config?.enableTime === true

      // Determine appropriate format based on whether time is enabled
      const displayFormat = hasTime ? 'd/m/Y H:i' : 'd/m/Y'
      const internalFormat = hasTime ? 'Y-m-d H:i' : 'Y-m-d'

      // Merge user config with our display format settings
      return {
        // Always use altInput to show the formatted date to user
        altInput: true,
        altFormat: displayFormat,
        // Keep the original dateFormat for the v-model binding
        dateFormat: internalFormat,
        ...this.config,
      }
    },
    hasTimeEnabled() {
      return this.config?.enableTime === true
    },
  },
  watch: {
    value(newVal) {
      if (this.date !== newVal) {
        this.date = newVal
      }
    },
  },
  methods: {
    onChange(selectedDates, dateStr) {
      if (this.config?.mode === 'range') {
        if (selectedDates.length === 2) {
          // Check if dates are same
          const isSameDate = selectedDates[0].getTime() === selectedDates[1].getTime()

          if (isSameDate) {
            const formatted = this.formatDate(selectedDates[0])
            const preparedDate = `${formatted} to ${formatted}`
            this.$emit('input', preparedDate)
          } else {
            const start = this.formatDate(selectedDates[0])
            const end = this.formatDate(selectedDates[1])
            const preparedDate = `${start} to ${end}`
            this.$emit('input', preparedDate)
          }
        }
      } else {
        // Check if we're using MonthSelectPlugin by examining the config
        const isMonthSelect = this.config?.plugins
                             && Array.isArray(this.config.plugins)
                             && this.config.plugins.some(p => p.constructor && p.constructor.name === 'MonthSelectPlugin')

        if (selectedDates && selectedDates.length > 0) {
          // If using MonthSelectPlugin, dateStr will already be in the correct format (m/Y)
          if (isMonthSelect) {
            this.$emit('input', dateStr)
          } else {
            const formatted = this.formatDate(selectedDates[0])
            this.$emit('input', formatted)
          }
        } else {
          this.$emit('input', '')
        }
      }
    },
    onDateClear() {
      this.date = ''
      this.$emit('input', '')
    },
    formatDate(date) {
      if (!date) return ''

      const d = new Date(date)
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')

      // Include time if enabled
      if (this.hasTimeEnabled) {
        const hours = String(d.getHours()).padStart(2, '0')
        const minutes = String(d.getMinutes()).padStart(2, '0')
        return `${year}-${month}-${day} ${hours}:${minutes}`
      }

      return `${year}-${month}-${day}`
    },
  },
}
</script>

<style scoped>
.flat-picker-clear-btn {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
</style>
